/**
 * Records client-side warnings and errors into a ring buffer so a user can attach them to a problem
 * report (see `ReportProblemDialog`). The browser keeps no console history of its own, so we have to
 * collect it while it happens.
 *
 * IMPORTANT - this module must have NO imports and must be imported first in `client/startup.ts`:
 * `@adornis/base/logging.ts` captures references to `console.warn`/`console.error` in its
 * `consoleLevelMap` when that module is evaluated. Everything the app logs through `logger.*` goes
 * through those frozen references, so our wrappers only end up in that map if we install them before
 * `logging.ts` is evaluated.
 *
 * It deliberately lives outside `client/` so the server-side test run can import it (an import from
 * a `client/` directory resolves to undefined on the server).
 */

export type RecordedLevel = 'warn' | 'error';

interface RecordedEntry {
  time: string;
  level: RecordedLevel;
  message: string;
}

const MAX_ENTRIES = 200;
const MAX_MESSAGE_LENGTH = 2000;

const entries: RecordedEntry[] = [];

const formatArgument = (argument: unknown): string => {
  if (typeof argument === 'string') return argument;
  if (argument instanceof Error) return `${argument.name}: ${argument.message}\n${argument.stack ?? ''}`.trimEnd();
  if (argument === null || argument === undefined || typeof argument !== 'object') return String(argument);
  try {
    // circular references and BigInt make this throw
    return JSON.stringify(argument) ?? String(argument);
  } catch {
    return String(argument);
  }
};

/** Formats and appends one entry, dropping the oldest one once the buffer is full. */
export const recordEntry = (level: RecordedLevel, args: unknown[]) => {
  const message = args.map(formatArgument).join(' ');
  entries.push({
    time: new Date().toISOString(),
    level,
    message: message.length > MAX_MESSAGE_LENGTH ? `${message.slice(0, MAX_MESSAGE_LENGTH)}… (gekürzt)` : message,
  });
  if (entries.length > MAX_ENTRIES) entries.shift();
};

/** The recorded log as plain text, ready to be shown to the user and attached to a mail. */
export const getRecordedConsole = () => {
  if (!entries.length) return 'Keine Warnungen oder Fehler aufgezeichnet.';
  return entries.map(entry => `[${entry.time}] ${entry.level.toUpperCase()} ${entry.message}`).join('\n');
};

export const getRecordedConsoleCount = () => entries.length;

/** Only used by tests. */
export const clearRecordedConsole = () => {
  entries.length = 0;
};

if (typeof window !== 'undefined') {
  for (const level of ['warn', 'error'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      recordEntry(level, args);
      original(...args);
    };
  }

  window.addEventListener('error', event => recordEntry('error', [event.error ?? event.message]));
  window.addEventListener('unhandledrejection', event =>
    recordEntry('error', ['Unbehandelte Promise-Ablehnung:', event.reason]),
  );
}
